import { FunctionComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { route, RoutableProps } from 'preact-router';
import { Layout } from '../components/layout';
import { setNewPassword, validateResetToken } from '../services/reset-password.service';
import { hashPassword } from '../lib/password';

interface Props extends RoutableProps {
  id?: string;
  email?: string;
  token?: string;
  locale?: string;
}

type Phase = 'validating' | 'invalid' | 'ready' | 'submitting' | 'done';

export const ResetPasswordPage: FunctionComponent<Props> = ({ id, email, token }) => {
  const [phase, setPhase] = useState<Phase>('validating');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id || !email || !token) {
      setPhase('invalid');
      return;
    }
    validateResetToken(token).then((valid) => setPhase(valid ? 'ready' : 'invalid'));
  }, [id, email, token]);

  const submit = async (e: Event) => {
    e.preventDefault();
    if (!id || !token) return;

    if (password.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }
    if (!/[A-Z]/.test(password) || !/[^A-Za-z0-9]/.test(password)) {
      setError('Passwort muss mind. einen Großbuchstaben und ein Sonderzeichen enthalten.');
      return;
    }
    if (/[äöüßÄÖÜ]/.test(password)) {
      setError('Umlaute (ä, ö, ü, ß) sind im Passwort nicht erlaubt.');
      return;
    }
    if (password !== confirm) {
      setError('Passwörter stimmen nicht überein.');
      return;
    }

    setError(null);
    setPhase('submitting');

    try {
      const hash = await hashPassword(password);
      await setNewPassword(token, parseInt(id, 10), hash);
      route('/reset-password/done', true);
    } catch {
      setError('Passwort konnte nicht zurückgesetzt werden. Link evtl. abgelaufen.');
      setPhase('ready');
    }
  };

  if (phase === 'validating') {
    return (
      <Layout title="Passwort zurücksetzen">
        <div class="center mt-lg muted">Link wird geprüft …</div>
      </Layout>
    );
  }

  if (phase === 'invalid') {
    return (
      <Layout title="Link ungültig" subtitle="Der Link ist abgelaufen oder fehlerhaft.">
        <button class="btn mt-md" onClick={() => route('/forgot-password')}>
          Neuen Link anfordern
        </button>
      </Layout>
    );
  }

  return (
    <Layout title="Neues Passwort" subtitle={email ? decodeURIComponent(email) : ''}>
      <form onSubmit={submit}>
        <div class="input-group">
          <label for="pw">Neues Passwort</label>
          <input
            id="pw"
            type="password"
            value={password}
            onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
            autocomplete="new-password"
            required
            disabled={phase === 'submitting'}
          />
          <div class="hint">Mind. 8 Zeichen, 1 Großbuchstabe, 1 Sonderzeichen. Keine Umlaute.</div>
        </div>

        <div class="input-group">
          <label for="confirm">Passwort wiederholen</label>
          <input
            id="confirm"
            type="password"
            value={confirm}
            onInput={(e) => setConfirm((e.target as HTMLInputElement).value)}
            autocomplete="new-password"
            required
            disabled={phase === 'submitting'}
          />
        </div>

        {error && <div class="error">{error}</div>}

        <button type="submit" class="btn" disabled={phase === 'submitting'}>
          {phase === 'submitting' ? <span class="spinner" /> : 'Passwort speichern'}
        </button>
      </form>
    </Layout>
  );
};
